import * as React from "react";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";

import Grid from "src/library/components/Hedron";
import type { IStore } from "src/core/shared/store";
import { MenuContext } from "src/core/shared/hooks/menu/useMenu";
import useList from "src/core/lists/hooks/useList";
import { useOrder } from "src/core/orders/hooks";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext";
import { IProductMaybeWithSubstitution } from "src/core/products/utils/filter-product-substitutions";
import useDrop from "src/core/drops/hooks/useDrop";
import ProductTagList from "src/modules/product-tags/components/ProductTagList";
import { OrderOrigin } from "src/core/orders/api/orders.api";
import { breakpoint } from "src/library/utils/styled-media-queries";
import {
  Text,
  TextTruncate
} from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Tooltip } from "src/library/components/Tooltip";
import {
  ErrorLevel,
  IProduct
} from "src/core/products/api/products.api";

import { ProductActions } from "./ProductActions";
import { IQuantityProps } from "./ProductQuantityButton";
import { useQuickViewProduct } from "../QuickViewProduct";
import { ProductComponentLayoutDetails } from "./ProductComponentLayoutDetails";
import { getProductRelatedGroupIcon } from "../../utils";

export interface IProductDetailsProps extends Partial<IQuantityProps> {
  product: IProductMaybeWithSubstitution;
  unavailable?: string | null;
  className?: string;
  onOrderModalChange?: (isOpen: boolean) => void;
  orderModalPosition?: "left" | "right" | "overlay";
  view?: "grid" | "compact";
  originalProduct?: IProduct;
}

export const ProductDetails: React.ReactFCWithChildren<IProductDetailsProps> = ({
  product,
  unavailable,
  className,
  onOrderModalChange,
  orderModalPosition,
  view = "grid",
  originalProduct
}) => {
  const [ { drop } ] = useDrop();
  const [ { context } ] = React.useContext(MenuContext);
  const [ { isCreatingOrder, currentOrder }, { createOrder } ] = useOrder();
  const [ { currentList } ] = useList();

  // Get substituted product if exists

  const {
    currentRole, installedModules, componentLayout, productRelatedGroups
  } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    installedModules: global.settings ? global.settings.installedModules : [],
    componentLayout: global.settings?.componentLayout,
    productRelatedGroups: global?.settings?.productRelatedGroups || []
  }));

  const [ autoFocus, setAutoFocus ] = React.useState<boolean>(false);
  const { setQuickViewProduct } = useQuickViewProduct();
  const productCardLayout = componentLayout?.productCard;

  /**
   * If the customer is ordering or browsing
   */
  const currentContext: "ordering" | "browsing" = React.useMemo(() => {
    let currentContextObject;

    if (context[ 0 ]) {
      if (context[ 0 ].type === "ORDER_PRODUCT_SELECTION") {
        currentContextObject = currentOrder;
      } else if (context[ 0 ].type === "LIST_PRODUCT_SELECTION") {
        currentContextObject = currentList;
      }
    }

    return currentContextObject && currentContextObject?.mode === "edit" ? "ordering" : "browsing";
  }, [
    context,
    currentOrder,
    currentList
  ]);

  // The related group associated to the product
  const relatedGroup = React.useMemo(() => {
    if (product.relatedGroupId) {
      return productRelatedGroups.find(rg => rg.id === product.relatedGroupId) || null;
    }

    return null;
  }, [ product.relatedGroupId, productRelatedGroups ]);

  // If parentRelatedGroup is null, the its assumed the product is the parent
  const parentRelatedGroup = React.useMemo(() => {
    if (product.relatedGroupId) {
      if (relatedGroup?.parentId) {
        return productRelatedGroups.find(rg => rg.id === relatedGroup.parentId) || null;
      }
    }

    return null;
  }, [
    product.relatedGroupId,
    productRelatedGroups,
    relatedGroup?.parentId
  ]);

  const CreateOrderContextButton = React.useCallback((product: IProduct) => (
    <>
      {drop && (
        <CreateOrderContext
          icon={false}
          direction={orderModalPosition || "left"}
          drop={drop}
          product={product}
          loading={isCreatingOrder}
          onOrderModalChange={isOpen => {
            if (onOrderModalChange) {
              onOrderModalChange(isOpen);
            }
          }}
          createDraftOrder={date => {
            setAutoFocus(true);
            createOrder(date, OrderOrigin.Manual, [ product ]);
          }}
        />
      )}
    </>
  ), [
    createOrder,
    drop,
    isCreatingOrder,
    onOrderModalChange,
    orderModalPosition
  ]);

  return (
    <ProductDetailsWrapper
      className={`${className} ${view}`}
      $tagsExist={product.tags?.length}
    >
      <div className="details-top">
        {installedModules.includes("product-tag") && product.tags && view === "grid" && (
          <div className="product-card-tags">
            <ProductTagList productTags={product.tags} />
          </div>
        )}

        <div className="product-info-wrapper">
          <div className="product-info">
            <div
              className="description-wrapper"
              onClick={() => setQuickViewProduct(product.id)}
            >
              {productCardLayout?.titleWrap === "truncate" || typeof productCardLayout?.titleWrap === "number" ? (
                <TextTruncate
                  truncateProps={{ lines: typeof productCardLayout.titleWrap === "number" ? productCardLayout.titleWrap : 2 }}
                  className="description"
                >
                  {product.description}
                </TextTruncate>
              ) : (
                <Text className="description">
                  {product.description}
                </Text>
              )}
            </div>

            {view !== "compact" && (
              <Text
                color="secondary"
                weight="bold"
                textTransform="uppercase"
                letterSpacing="big"
                className="part-number"
              >
                {`#${product.partNumber}`}
              </Text>
            )}

            {view !== "compact" && <ProductComponentLayoutDetails product={product} />}
          </div>

          {installedModules.includes("product-related-group") &&
            relatedGroup &&
            parentRelatedGroup?.settings?.childProducts?.errorLevel === ErrorLevel.Error && (
              <>
                <Tooltip
                  id={`${product.id}-warning-tooltip`}
                  type="dark"
                  place="left"
                  getContent={() => (
                    <>
                      <Text color="white">
                        {`This product requires the equivilent amount of ${parentRelatedGroup?.terminology.plural} in the order.`}
                      </Text>
                    </>
                  )}
                />

                <Icon
                  icon={getProductRelatedGroupIcon(parentRelatedGroup.settings)}
                  $fillColor="warn"
                  size={25}
                  data-tip
                  data-for={`${product.id}-warning-tooltip`}
                />
              </>
          )}
        </div>
      </div>

      <div className="details-bottom">
        <ProductActions
          product={originalProduct || product}
          unavailable={unavailable}
          viewContext={currentContext}
          originalProduct={originalProduct}
          CreateOrderButton={CreateOrderContextButton}
          currentRole={currentRole}
          autoFocus={autoFocus}
          installedModules={installedModules}
        />
      </div>
    </ProductDetailsWrapper>
  );
};

const ProductDetailsWrapper = styled(Grid.Box)<{ $tagsExist: boolean }>`
  ${({ theme, $tagsExist }) => css`
    background-color: ${theme.colors.white};
    flex-grow: 1;
    padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md} ${theme.spacing.padding.md} ${theme.spacing.padding.md};
    border-top: solid 2px ${theme.colors.lightGrey};
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &.compact {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      border-top: none;

      .product-card-actions {
        ${breakpoint("sm", theme)} {
          width: 100%;
          align-items: center;
          display: flex;
          justify-content: flex-end;
        }
      }

      .details-top {
        width: 100%;
      }
    }

    .details-bottom {
      width: 100%;
    }

    .product-card-tags {
      min-height: ${$tagsExist ? "30px" : "30px"};
      margin-bottom: ${theme.spacing.margins.sm};
    }

    &.not(.compact) {
      .product-info {
        margin-bottom: ${theme.spacing.margins.sm};
      }
    }

    .product-info-wrapper {
      display: flex;
      justify-content: space-between;
    }

    .product-info {
      .description-wrapper {
        cursor: pointer;
      }

      .product-detail-configurable-item {
        span {
          color: ${theme.fonts.colors.secondary};
        }
      }

      .description {
        display: block;
        text-overflow: ellipsis;
        width: 100%;
        overflow: hidden;
        color: ${theme.colors.primaryDark};
        font-weight: ${theme.fonts.weights.bold};
        text-align: left;
        font-family: ${theme.fonts.family.heading};
      }

      button {
        padding: 0;
      }
    }
  `}
`;
